import { useCallback, useRef } from "react";
import { FiImage, FiX } from "react-icons/fi";
import { cn, isVideoFile } from "@/shared/lib";

export interface ImageUploadProps {
  file: File | null;
  previewUrl: string | null;
  onFileChange: (file: File | null) => void;
  variant?: "button" | "input";
  accept?: string;
}

export const ImageUpload: React.FunctionComponent<ImageUploadProps> = ({
  file,
  previewUrl,
  onFileChange,
  variant = "input",
  accept = "image/*,video/*",
}: ImageUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasPreview = !!previewUrl;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    onFileChange(selectedFile || null);
  };

  const handleButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const clearAll = useCallback((): void => {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onFileChange]);

  const isVideo = file ? isVideoFile(file.name, file.type) : false;

  return (
    <div className="space-y-2">
      {/* ファイル入力 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className={cn(
          variant === "button"
            ? "hidden"
            : "w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-card file:text-foreground file:cursor-pointer hover:file:bg-muted",
          hasPreview && "hidden",
        )}
      />

      {/* 画像選択ボタン（variant="button" かつプレビューなし時のみ表示） */}
      {variant === "button" && !hasPreview && (
        <button
          type="button"
          onClick={handleButtonClick}
          className="w-full px-4 py-6 border-2 border-dashed border-border rounded-lg bg-card hover:bg-muted transition-colors text-muted-foreground flex items-center justify-center gap-2"
        >
          <FiImage className="w-5 h-5" />
          画像を選択
        </button>
      )}

      {/* プレビュー + 削除ボタン */}
      {hasPreview && (
        <div className="relative inline-block mt-3">
          <button
            type="button"
            onClick={clearAll}
            className="absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center bg-destructive text-destructive-foreground rounded-full"
            aria-label="画像を削除"
          >
            <FiX className="w-4 h-4" />
          </button>
          {isVideo ? (
            <video
              src={previewUrl}
              className="max-h-32 rounded-lg"
              controls
              muted
            />
          ) : (
            <img
              src={previewUrl}
              alt="プレビュー"
              className="max-h-32 rounded-lg"
            />
          )}
        </div>
      )}
    </div>
  );
};
